import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * BES-9535: guest copy for a structured charge failure, translated.
 *
 * The backend returns an English `error` plus a `failureReason` code, and `api.ts` puts the
 * code on the thrown error as `code`. The card modal mapped those codes to `t()` strings, but
 * the card-on-file paths in Upsells and DamageWaiver rendered `err.message` straight through,
 * so a guest reading the portal in Spanish got English on exactly the failures this PR added.
 * Shared here so every catch handler maps the same codes to the same translated strings.
 *
 * Mirrors `backend/guest-portal/backend/chargeFailures.ts`. Keep the two in step: the backend
 * copy is the fallback for clients that do not map the code.
 */
/**
 * BES-9535: failure codes that leave the upsell folio line POSTED. Retrying one of these adds
 * ANOTHER line rather than replacing it, so any surface offering a retry has to withhold it:
 * the card modal keeps Pay down, and the damage-waiver failure screen drops its Try Again.
 *
 * Mirrors the backend `CARD_FORM_CANNOT_HELP` denylist minus `guesty_validation`, which
 * reverses its line and is safe to attempt again. Defined here rather than per-component so
 * the surfaces cannot drift apart on which codes are safe.
 */
export const RETRY_LEAVES_LIVE_LINE = new Set(['ambiguous_charge_result', 'charge_rejected_line_orphaned']);

export function useChargeErrorCopy(): (code: string | undefined, fallback?: string) => string {
  const { t } = useTranslation();
  // Literal strings live inside t() so i18next-parser can extract them statically.
  return useCallback(
    (code: string | undefined, fallback?: string): string => {
      switch (code) {
        case 'non_partner_payment_method':
        case 'no_payment_method':
          return t('Please re-enter your card to complete this purchase.');
        case 'card_declined':
          return t('Your card was declined. Please try another card.');
        case 'network_error':
        case 'guesty_5xx':
          return t('We hit a temporary issue processing your payment. Please try again.');
        case 'ambiguous_charge_result':
          // Charge state unconfirmed and the folio line was kept; do not invite a
          // retry (it could double-charge). Ops reconciles this.
          return t('Your payment is being confirmed. Please do not retry. Contact support if you do not see a confirmation shortly.');
        case 'charge_rejected_line_orphaned':
          // BES-9535: nothing was charged, but a retry would leave a second folio line.
          return t('We could not complete this purchase and you have not been charged. Please message us and we will add it for you.');
        case 'guesty_validation':
          // BES-9535: the PMS refused the charge, not the card. A retry is safe.
          return t('We could not complete this purchase right now. Please try again in a few minutes, or message us and we will add it for you.');
        case 'retry_cap_exceeded':
          // BES-9460: capped after three failures in 24h. Nothing charged, no line posted.
          return t('We could not complete this purchase after several attempts and you have not been charged. Please message us and we will add it for you.');
        case 'airbnb_id_missing':
          // BES-9552: no Airbnb reservation id, so there is no Resolution Center link to send.
          return t('We could not set up an Airbnb payment for this booking. Please message us and we will add it for you.');
        case 'record_failed':
          // BES-9552: we could not record the confirmation, so the guest must not be sent to
          // pay yet. Retrying is safe: nothing was charged and no record was written.
          return t('We could not set this up just now. Please try again in a moment.');
        case 'charge_lock_timeout':
        case 'charge_lock_lost':
          // Another purchase held the reservation. Nothing was charged and no line remains.
          return t('Another update to your booking was in progress. Please try again in a moment.');
        default:
          return fallback || t('Payment failed');
      }
    },
    [t]
  );
}
